import { newId } from './ids';
import type { Benefit, Card, Perk, Program, Redemption } from './schema';
import { BENEFIT_PERIODS, PERK_KINDS } from './schema-consts';
import { PROGRAMS } from './valuations';

/**
 * Reads the wallet summary Claude writes in claude.ai (see WALLET_PROMPT) into records.
 * Forgiving on purpose: names instead of ids, "$795" instead of 795, missing fields.
 */

type New<T> = Omit<T, 'rev' | 'updatedAt' | 'createdAt' | 'deleted'>;

export interface WalletImport {
  programs: New<Program>[];
  cards: New<Card>[];
  perks: New<Perk>[];
  redemptions: New<Redemption>[];
  /** Lines that couldn't be used, to show. */
  skipped: string[];
}

export const WALLET_PROMPT = `From everything you know about me, list my travel rewards as JSON only, no prose, in this shape (leave out anything you don't know; dates as YYYY-MM-DD, money in US dollars as numbers):
{
  "programs": [{ "program": "World of Hyatt", "balance": 61450, "status": "Globalist", "expires": null }],
  "cards": [{ "name": "Sapphire Reserve", "issuer": "Chase", "last4": "1234", "annualFee": 795, "feeDue": "2026-11-18", "opened": "2022-11-18", "earns": "Chase Ultimate Rewards",
    "benefits": [{ "title": "Dining credit", "value": 150, "period": "semiannual", "resets": "2026-12-31", "used": 0, "note": null }] }],
  "perks": [{ "kind": "certificate", "title": "Marriott free night up to 35k", "program": "Marriott Bonvoy", "value": null, "expires": "2026-11-30", "used": false }],
  "bonuses": [{ "title": "Platinum welcome bonus", "card": "Platinum Card", "points": 80000, "spent": 4200, "target": 8000, "deadline": "2026-11-15" }],
  "redemptions": [{ "program": "World of Hyatt", "date": "2026-06-12", "points": 25000, "cashValue": 640, "fees": 0, "what": "Hyatt Regency Lisbon, 2 nights" }]
}
period is one of: monthly, quarterly, semiannual, annual, cardmember-year, once, ongoing. kind is one of: credit, certificate, upgrade, voucher, other. Never include full card numbers.`;

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Program id from a name like "Hyatt", "World of Hyatt" or "hyatt". */
export function matchProgram(name: unknown): { id: string; name: string } | null {
  if (typeof name !== 'string' || !name.trim()) return null;
  const n = norm(name);
  const exact = PROGRAMS.find((p) => norm(p.id) === n || norm(p.name) === n);
  if (exact) return { id: exact.id, name: exact.name };
  const ALIASES: [string, string][] = [
    ['membershiprewards', 'amex_mr'],
    ['amex', 'amex_mr'],
    ['ultimaterewards', 'chase_ur'],
    ['chase', 'chase_ur'],
    ['thankyou', 'citi_ty'],
    ['capitalone', 'capital_one'],
    ['venture', 'capital_one'],
    ['bilt', 'bilt'],
    ['bonvoy', 'marriott'],
    ['marriott', 'marriott'],
    ['hyatt', 'hyatt'],
    ['hilton', 'hilton'],
    ['ihg', 'ihg'],
    ['skymiles', 'delta'],
    ['delta', 'delta'],
    ['aadvantage', 'american'],
    ['mileageplus', 'united'],
    ['united', 'united'],
    ['southwest', 'southwest'],
    ['rapidrewards', 'southwest'],
    ['alaska', 'alaska'],
    ['atmos', 'alaska'],
    ['aeroplan', 'aeroplan'],
    ['flyingblue', 'flyingblue'],
    ['avios', 'avios'],
    ['virgin', 'virgin'],
    ['trueblue', 'jetblue'],
    ['wyndham', 'wyndham'],
    ['choice', 'choice'],
    ['accor', 'accor'],
    ['emirates', 'emirates'],
    ['krisflyer', 'krisflyer'],
    ['turkish', 'turkish'],
    ['wellsfargo', 'wells_fargo'],
  ];
  const hit = ALIASES.find(([a]) => n.includes(a));
  const p = hit && PROGRAMS.find((x) => x.id === hit[1]);
  return p ? { id: p.id, name: p.name } : { id: 'other', name: name.trim() };
}

const money = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v.replace(/[$,\s]/g, ''));
    return Number.isFinite(n) && v.trim() !== '' ? n : null;
  }
  return null;
};
const day = (v: unknown): string | null =>
  typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null;
const text = (v: unknown, max = 160): string =>
  typeof v === 'string' ? v.trim().slice(0, max) : '';
const list = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v)
    ? v.filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
    : [];

/** Pulls the first JSON object out of pasted text, even with prose or code fences around it. */
function extractJson(input: string): unknown {
  const start = input.indexOf('{');
  const end = input.lastIndexOf('}');
  if (start < 0 || end <= start) throw new Error("That doesn't look like the JSON from Claude.");
  return JSON.parse(input.slice(start, end + 1));
}

export function parseWalletImport(input: string, today: string): WalletImport {
  const data = extractJson(input) as Record<string, unknown>;
  const out: WalletImport = { programs: [], cards: [], perks: [], redemptions: [], skipped: [] };

  for (const p of list(data.programs)) {
    const prog = matchProgram(p.program ?? p.name);
    const balance = money(p.balance);
    if (!prog || balance == null) {
      out.skipped.push(`Points: ${text(p.program ?? p.name) || 'unnamed'}`);
      continue;
    }
    out.programs.push({
      id: newId('prg'),
      programId: prog.id,
      name: prog.name,
      balance: Math.max(0, Math.round(balance)),
      tier: text(p.status ?? p.tier, 60) || null,
      expiresOn: day(p.expires),
      checkedOn: today,
      notes: text(p.notes, 4000),
    });
  }

  const cardIds = new Map<string, string>();
  for (const c of list(data.cards)) {
    const name = text(c.name, 120);
    if (!name) {
      out.skipped.push('A card with no name');
      continue;
    }
    // Only an exact last 4; anything longer (a full number) is dropped, never cut down.
    const last4 = typeof c.last4 === 'string' ? c.last4.trim() : '';
    const benefits: Benefit[] = list(c.benefits)
      .map((b, i) => ({
        id: `b${i + 1}`,
        title: text(b.title),
        value: money(b.value),
        period: (BENEFIT_PERIODS as readonly string[]).includes(String(b.period))
          ? (b.period as Benefit['period'])
          : 'annual',
        resetsOn: day(b.resets),
        used: money(b.used),
        note: text(b.note, 600) || null,
      }))
      .filter((b) => b.title);
    const id = newId('crd');
    cardIds.set(norm(name), id);
    out.cards.push({
      id,
      name,
      issuer: text(c.issuer, 60),
      last4: /^\d{4}$/.test(last4) ? last4 : null,
      annualFee: money(c.annualFee) ?? 0,
      feeDueOn: day(c.feeDue),
      openedOn: day(c.opened),
      earns: matchProgram(c.earns)?.id ?? null,
      benefits,
      notes: text(c.notes, 4000),
    });
  }

  for (const p of list(data.perks)) {
    const title = text(p.title);
    if (!title) continue;
    const kind = (PERK_KINDS as readonly string[]).includes(String(p.kind))
      ? (p.kind as Perk['kind'])
      : 'other';
    const value = money(p.value);
    out.perks.push({
      id: newId('prk'),
      kind,
      title,
      programId: matchProgram(p.program)?.id ?? null,
      cardId: typeof p.card === 'string' ? (cardIds.get(norm(p.card)) ?? null) : null,
      value,
      unit: value == null ? null : 'usd',
      progress: null,
      expiresOn: day(p.expires),
      used: p.used === true,
      notes: text(p.notes, 2000),
    });
  }

  for (const b of list(data.bonuses)) {
    const title = text(b.title);
    if (!title) continue;
    const target = money(b.target);
    const card = typeof b.card === 'string' ? cardIds.get(norm(b.card)) : undefined;
    out.perks.push({
      id: newId('prk'),
      kind: 'bonus',
      title,
      programId: matchProgram(b.program)?.id ?? null,
      cardId: card ?? null,
      value: money(b.points),
      unit: money(b.points) == null ? null : 'points',
      progress: target ? { done: money(b.spent) ?? 0, target } : null,
      expiresOn: day(b.deadline),
      used: b.done === true,
      notes: text(b.notes, 2000),
    });
  }

  for (const r of list(data.redemptions)) {
    const prog = matchProgram(r.program);
    const points = money(r.points);
    const on = day(r.date);
    if (!prog || points == null || !on) {
      out.skipped.push(`Redemption: ${text(r.what) || 'unnamed'}`);
      continue;
    }
    out.redemptions.push({
      id: newId('rdm'),
      programId: prog.id,
      on,
      points: Math.round(points),
      cashValue: money(r.cashValue),
      fees: money(r.fees),
      title: text(r.what, 200) || prog.name,
      tripId: null,
      notes: text(r.notes, 2000),
    });
  }
  return out;
}
